import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import debounce from "debounce";
import Swal from "sweetalert2";
import { format } from "date-fns";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import TeacherProfileLink from "components/TeacherProfileLink";
import TeacherAvatar from "components/TeacherAvatar";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Select2Field from "components/Select2Field";
import Loading from "components/Loading";
import TablePaginationBar from "components/TablePaginationBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import { parseListPayload, totalPagesFromCount } from "utils/paginationStats";
import {
  fetchTeacherLicenseSummary,
  fetchTeacherLicenseList,
  activateTeachers,
  deactivateTeachers,
  isLicenseApiFailure,
  getLicenseApiMessage,
  normalizeLicenseActionBody,
  formatLicenseApiDebug,
} from "services/teacherLicenseService";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import { parseTeacherLicenseSummary } from "layouts/dashboard/dashboardChartHelpers";
import { readIsTeacher, readIsLicensed, resolveTeacherDatabaseId } from "utils/teacherFlags";
import { useSoftUIController } from "context";
import { isObserver } from "assistant/userRole";

const GEO_API = {
  provinces: `${apiurl}Assignment/provinces`,
  departments: (provid) => `${apiurl}Assignment/departments?provid=${provid}`,
  subprefectures: (dptid) => `${apiurl}Assignment/subprefectures?dptid=${dptid}`,
  establishments: (sptid) => `${apiurl}Assignment/establishments?sptid=${sptid}`,
};

const STATUS_ALL = "all";
const STATUS_LICENSED = "licensed";
const STATUS_PENDING = "pending";

const DEFAULT_FILTERS = {
  province: "",
  department: "",
  subprefecture: "",
  establishment: "",
  status: STATUS_PENDING,
  matricule: "",
};

const swalApp = {
  confirmButtonColor: figmaTokens.accent,
  background: figmaTokens.surface,
  color: figmaTokens.textPrimary,
};

const statWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 108,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
  },
};

const tableShellSx = {
  mx: { xs: 1.25, md: 2 },
  mb: 0,
  borderRadius: "14px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  overflow: "auto",
  maxHeight: { xs: "65vh", md: "min(68vh, 680px)" },
  bgcolor: figmaTokens.surface,
  boxShadow: "0 10px 40px rgba(15, 23, 42, 0.07)",
};

const filterLabelSx = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

const makeOptions = (data, ids, labels) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    id: ids.map((key) => item[key]).find(Boolean),
    text: labels.map((key) => item[key]).find(Boolean),
  }));

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function formatDate(value) {
  if (!value) return "--";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, "yyyy-MM-dd");
}

function teacherFullName(row) {
  const first = row?.preName ?? row?.PreName ?? "";
  const last = row?.name ?? row?.Name ?? "";
  return [first, last].filter(Boolean).join(" ") || "--";
}

function statusToApiParam(status) {
  if (status === STATUS_LICENSED) return true;
  if (status === STATUS_PENDING) return false;
  return undefined;
}

function licenseChipSx(isLicensed) {
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 28,
    borderRadius: "999px",
    "& .MuiChip-label": { px: 1.35 },
  };
  if (isLicensed) {
    return {
      ...base,
      color: "#fff",
      bgcolor: "#16a34a",
      border: "none",
      boxShadow: "0 4px 12px rgba(22, 163, 74, 0.35)",
    };
  }
  return {
    ...base,
    color: "#92400e",
    bgcolor: "#fef3c7",
    border: `1px solid #fbbf24`,
    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.2)",
  };
}

function buildGeoBody(filters) {
  const body = {};
  if (filters.province) body.provinceId = Number(filters.province);
  if (filters.department) body.departmentId = Number(filters.department);
  if (filters.subprefecture) body.subPrefectureId = Number(filters.subprefecture);
  if (filters.establishment) body.establishmentId = Number(filters.establishment);
  return body;
}

function hasGeoFilter(filters) {
  return Boolean(
    filters.province || filters.department || filters.subprefecture || filters.establishment
  );
}

function extractApiMessage(err, fallback) {
  return err?.response?.data?.message ?? err?.response?.data?.Message ?? err?.message ?? fallback;
}

function TeacherLicense() {
  const [controller] = useSoftUIController();
  const readOnly = isObserver(controller.user);
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({
    licensedCount: 0,
    unlicensedCount: 0,
    totalTeachers: 0,
    maxLicensedTeachers: 0,
    remainingLicenses: 0,
  });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalResults, setTotalResults] = useState(0);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);
  const [estabOpts, setEstabOpts] = useState([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);

  const cache = useRef({ provinces: null, departments: {}, subprefectures: {}, establishments: {} });
  const controllers = useRef({});

  const cancelRequest = (key) => {
    if (controllers.current[key]) controllers.current[key].abort();
    controllers.current[key] = new AbortController();
    return controllers.current[key].signal;
  };

  const showToast = useCallback(
    async (icon, message, detail) => {
      await Swal.fire({
        ...swalApp,
        icon,
        title: message,
        ...(detail
          ? {
              html: `<pre style="margin:0;text-align:left;max-height:420px;overflow:auto;font-size:11px;line-height:1.45;white-space:pre-wrap;word-break:break-word;font-family:Consolas,Monaco,monospace;">${String(detail)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")}</pre>`,
              width: 720,
            }
          : {}),
        timer: icon === "success" && !detail ? 2200 : undefined,
        showConfirmButton: icon !== "success" || Boolean(detail),
      });
    },
    []
  );

  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchTeacherLicenseSummary();
      setSummary(parseTeacherLicenseSummary(data));
    } catch (err) {
      console.error("Teacher license summary error", err);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        pageNumber: page,
        pageSize,
      };
      const geo = buildGeoBody(appliedFilters);
      Object.assign(params, geo);
      const licensedParam = statusToApiParam(appliedFilters.status);
      if (licensedParam !== undefined) params.isLicensed = licensedParam;

      const data = await fetchTeacherLicenseList(params);
      const list = parseListPayload(data);
      setRows(list);
      setTotalResults(
        Number(
          data?.stats?.totalResults ??
            list?.[0]?.totalResults ??
            list?.[0]?.TotalResults ??
            list.length
        )
      );
    } catch (err) {
      console.error("Teacher license list error", err);
      const msg = extractApiMessage(err, t("teacher_license.error.load", "Unable to load teachers."));
      setError(msg);
      setRows([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize, t]);

  const fetchProvinces = useCallback(async () => {
    if (cache.current.provinces) return setProvOpts(cache.current.provinces);
    try {
      const { data } = await axios.get(GEO_API.provinces, { headers: authHeaders() });
      const opts = makeOptions(data, ["provid", "id"], ["name", "province"]);
      cache.current.provinces = opts;
      setProvOpts(opts);
    } catch (err) {
      console.error("Fetch provinces error", err);
    }
  }, []);

  const fetchDepartments = useCallback(
    debounce(async (provid) => {
      if (!provid) return setDeptOpts([]);
      if (cache.current.departments[provid]) return setDeptOpts(cache.current.departments[provid]);
      try {
        const { data } = await axios.get(GEO_API.departments(provid), {
          signal: cancelRequest("departments"),
          headers: authHeaders(),
        });
        const opts = makeOptions(data, ["dptid", "id"], ["name", "department"]);
        cache.current.departments[provid] = opts;
        setDeptOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch departments error", err);
      }
    }, 300),
    []
  );

  const fetchSubprefectures = useCallback(
    debounce(async (dptid) => {
      if (!dptid) return setSubpOpts([]);
      if (cache.current.subprefectures[dptid]) return setSubpOpts(cache.current.subprefectures[dptid]);
      try {
        const { data } = await axios.get(GEO_API.subprefectures(dptid), {
          signal: cancelRequest("subprefectures"),
          headers: authHeaders(),
        });
        const opts = makeOptions(data, ["sptid", "id"], ["name", "subprefecture"]);
        cache.current.subprefectures[dptid] = opts;
        setSubpOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch subprefectures error", err);
      }
    }, 300),
    []
  );

  const fetchEstablishments = useCallback(
    debounce(async (sptid) => {
      if (!sptid) return setEstabOpts([]);
      if (cache.current.establishments[sptid]) return setEstabOpts(cache.current.establishments[sptid]);
      try {
        const { data } = await axios.get(GEO_API.establishments(sptid), {
          signal: cancelRequest("establishments"),
          headers: authHeaders(),
        });
        const opts = makeOptions(data, ["id"], ["name", "establishment"]);
        cache.current.establishments[sptid] = opts;
        setEstabOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch establishments error", err);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchProvinces();
    if (!readOnly) loadSummary();
  }, [fetchProvinces, loadSummary, readOnly]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filteredRows = useMemo(() => {
    const q = String(draftFilters.matricule || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      String(row?.regNumber ?? row?.RegNumber ?? "")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, draftFilters.matricule]);

  const totalPages = totalPagesFromCount(totalResults, pageSize);

  const geoScopeLabel = useCallback(
    (filters) => {
      const parts = [];
      if (filters.province) {
        parts.push(
          `${t("teacher_license.province", "Province")}: ${
            provOpts.find((o) => String(o.id) === String(filters.province))?.text ?? filters.province
          }`
        );
      }
      if (filters.department) {
        parts.push(
          `${t("teacher_license.department", "Department")}: ${
            deptOpts.find((o) => String(o.id) === String(filters.department))?.text ?? filters.department
          }`
        );
      }
      if (filters.subprefecture) {
        parts.push(
          `${t("teacher_license.subprefecture", "Sub-prefecture")}: ${
            subpOpts.find((o) => String(o.id) === String(filters.subprefecture))?.text ??
            filters.subprefecture
          }`
        );
      }
      if (filters.establishment) {
        parts.push(
          `${t("teacher_license.establishment", "Establishment")}: ${
            estabOpts.find((o) => String(o.id) === String(filters.establishment))?.text ??
            filters.establishment
          }`
        );
      }
      if (filters.status !== STATUS_ALL) {
        parts.push(
          `${t("teacher_license.status", "Status")}: ${
            filters.status === STATUS_LICENSED
              ? t("teacher_license.status.licensed", "Licensed")
              : t("teacher_license.status.pending", "Pending")
          }`
        );
      }
      return parts.length ? parts.join(" · ") : t("teacher_license.scope.all", "All teachers (no geographic filter)");
    },
    [deptOpts, estabOpts, provOpts, subpOpts, t]
  );

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
  };

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setDeptOpts([]);
    setSubpOpts([]);
    setEstabOpts([]);
    setPage(1);
  };

  const refreshAll = async () => {
    await Promise.all([loadSummary(), loadList()]);
  };

  const runLicenseAction = useCallback(
    async ({ fn, body, endpoint, debugContext = null }) => {
      setActionLoading(true);
      let toast = null;
      let shouldRefresh = false;
      let requestBody = body;
      try {
        try {
          requestBody = normalizeLicenseActionBody(body);
        } catch (normalizeErr) {
          requestBody = body;
          throw normalizeErr;
        }

        console.log("[TeacherLicense] request", endpoint, requestBody);
        const res = await fn(body);
        console.log("[TeacherLicense] response", endpoint, res);

        const msg = getLicenseApiMessage(res, t("teacher_license.updated", "Updated."));
        if (isLicenseApiFailure(res)) {
          const detail = formatLicenseApiDebug({
            endpoint,
            requestBody,
            status: 200,
            responseBody: res,
            context: debugContext,
          });
          toast = {
            icon: "error",
            message: msg || t("teacher_license.error.update", "Unable to update license."),
            detail,
          };
        } else {
          shouldRefresh = true;
          toast = { icon: "success", message: msg };
        }
      } catch (err) {
        console.error("[TeacherLicense] error", endpoint, err?.response?.data ?? err);
        const detail = formatLicenseApiDebug({
          endpoint,
          requestBody,
          status: err?.response?.status ?? null,
          responseBody: err?.response?.data ?? { error: err?.message },
          context: debugContext,
        });
        toast = {
          icon: "error",
          message: extractApiMessage(err, t("teacher_license.error.update", "Unable to update license.")),
          detail,
        };
      } finally {
        setActionLoading(false);
      }
      if (shouldRefresh) await refreshAll();
      if (toast) await showToast(toast.icon, toast.message, toast.detail);
    },
    [loadList, loadSummary, showToast, t]
  );

  const handleRowToggle = async (row) => {
    const { id: teacherId, source, regNumber } = resolveTeacherDatabaseId(row);
    if (teacherId == null) {
      await showToast(
        "error",
        t(
          "teacher_license.error.missing_db_id",
          "Could not resolve teacher database id from this row. Use the numeric id from the API list, not matricule."
        ),
        formatLicenseApiDebug({
          endpoint: "/TeacherLicense/activate",
          requestBody: null,
          status: null,
          responseBody: null,
          context: {
            rowIds: {
              id: row?.id,
              Id: row?.Id,
              teacherId: row?.teacherId,
              TeacherId: row?.TeacherId,
              regNumber: row?.regNumber ?? row?.RegNumber,
            },
            note: "Activate/deactivate requires { teacherId: <numeric DB id> }, not regNumber.",
          },
        })
      );
      return;
    }
    if (!readIsTeacher(row?.isTeacher ?? row?.IsTeacher)) return;
    const licensed = readIsLicensed(row?.isLicensed ?? row?.IsLicensed);
    const endpoint = licensed ? "/TeacherLicense/deactivate" : "/TeacherLicense/activate";
    await runLicenseAction({
      fn: licensed ? deactivateTeachers : activateTeachers,
      body: { teacherId },
      endpoint,
      debugContext: {
        action: licensed ? "deactivate" : "activate",
        idSource: source,
        sentTeacherId: teacherId,
        regNumber,
        isTeacher: row?.isTeacher ?? row?.IsTeacher,
        isLicensed: row?.isLicensed ?? row?.IsLicensed,
        note: "POST body uses numeric database id only — never matricule/regNumber.",
      },
    });
  };

  const openBulkConfirm = (activate) => {
    if (!hasGeoFilter(appliedFilters)) {
      Swal.fire({
        ...swalApp,
        icon: "info",
        title: t(
          "teacher_license.bulk.geo_required",
          "Select at least one geographic filter for bulk actions."
        ),
        text: t(
          "teacher_license.bulk.geo_note",
          "Bulk actions only affect teachers with an active establishment assignment in the selected area."
        ),
      });
      return;
    }
    setBulkAction(activate ? "activate" : "deactivate");
    setBulkConfirmOpen(true);
  };

  const handleBulkConfirm = async () => {
    setBulkConfirmOpen(false);
    const geoBody = buildGeoBody(appliedFilters);
    const endpoint =
      bulkAction === "activate" ? "/TeacherLicense/activate" : "/TeacherLicense/deactivate";
    await runLicenseAction({
      fn: bulkAction === "activate" ? activateTeachers : deactivateTeachers,
      body: geoBody,
      endpoint,
      debugContext: { action: bulkAction, appliedFilters },
    });
    setBulkAction(null);
  };

  const updateDraft = (patch) => setDraftFilters((prev) => ({ ...prev, ...patch }));

  return (
    <DashboardLayout>
      {(loading || actionLoading) && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }} className="content-area-main">
        <SoftBox px={{ xs: 2, md: 3 }} mb={2}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            {t("teacher_license.title", "Teacher Licenses")}
          </SoftTypography>
          <SoftTypography variant="body2" color="text" sx={{ mt: 0.5, color: figmaTokens.textMuted }}>
            {t(
              "teacher_license.subtitle",
              "Activate registered teachers before they can sign in or mark attendance. Registration is unlimited; only licensed teachers can use the app."
            )}
          </SoftTypography>
        </SoftBox>

        {!readOnly && <SoftBox px={{ xs: 2, md: 3 }} mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2.4} sx={statWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("teacher_license.stats.licensed", "Licensed") }}
                count={summary.licensedCount}
                percentage={{
                  color: "success",
                  text: summary.maxLicensedTeachers > 0 ? `/ ${summary.maxLicensedTeachers}` : "",
                }}
                icon={{ color: "success", component: "verified" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4} sx={statWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("teacher_license.stats.pending", "Pending activation") }}
                count={summary.unlicensedCount}
                percentage={{ color: "warning", text: "" }}
                icon={{ color: "warning", component: "pending_actions" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4} sx={statWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("teacher_license.stats.total", "Total registered") }}
                count={summary.totalTeachers}
                percentage={{ color: "info", text: "" }}
                icon={{ color: "info", component: "groups" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4} sx={statWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("teacher_license.stats.max_licensed", "Max licensed") }}
                count={summary.maxLicensedTeachers}
                percentage={{ color: "dark", text: "" }}
                icon={{ color: "dark", component: "badge" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4} sx={statWrapSx}>
              <MiniStatisticsCard
                title={{ text: t("teacher_license.stats.remaining", "Remaining licenses") }}
                count={summary.remainingLicenses}
                percentage={{ color: "warning", text: "" }}
                icon={{ color: "warning", component: "pie_chart" }}
              />
            </Grid>
          </Grid>
        </SoftBox>}

        <SoftBox px={{ xs: 2, md: 3 }} mb={2}>
          <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "16px", border: `1px solid ${figmaTokens.borderSubtle}` }}>
            <SoftTypography variant="h6" fontWeight="bold" mb={2}>
              {t("teacher_license.filters", "Filters")}
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography component="label" sx={filterLabelSx}>
                  {t("teacher_license.province", "Province")}
                </SoftTypography>
                <Select2Field
                  name="province"
                  options={[
                    { id: "", text: t("teacher_license.province_placeholder", "Select province") },
                    ...provOpts,
                  ]}
                  value={draftFilters.province}
                  onChange={(provid) => {
                    updateDraft({
                      province: provid,
                      department: "",
                      subprefecture: "",
                      establishment: "",
                    });
                    setDeptOpts([]);
                    setSubpOpts([]);
                    setEstabOpts([]);
                    fetchDepartments(provid);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography component="label" sx={filterLabelSx}>
                  {t("teacher_license.department", "Department")}
                </SoftTypography>
                <Select2Field
                  name="department"
                  options={[
                    { id: "", text: t("teacher_license.department_placeholder", "Select department") },
                    ...deptOpts,
                  ]}
                  value={draftFilters.department}
                  disabled={!draftFilters.province}
                  onChange={(dptid) => {
                    updateDraft({ department: dptid, subprefecture: "", establishment: "" });
                    setSubpOpts([]);
                    setEstabOpts([]);
                    fetchSubprefectures(dptid);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography component="label" sx={filterLabelSx}>
                  {t("teacher_license.subprefecture", "Sub-prefecture")}
                </SoftTypography>
                <Select2Field
                  name="subprefecture"
                  options={[
                    { id: "", text: t("teacher_license.subprefecture_placeholder", "Select sub-prefecture") },
                    ...subpOpts,
                  ]}
                  value={draftFilters.subprefecture}
                  disabled={!draftFilters.department}
                  onChange={(sptid) => {
                    updateDraft({ subprefecture: sptid, establishment: "" });
                    setEstabOpts([]);
                    fetchEstablishments(sptid);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography component="label" sx={filterLabelSx}>
                  {t("teacher_license.establishment", "Establishment")}
                </SoftTypography>
                <Select2Field
                  name="establishment"
                  options={[
                    { id: "", text: t("teacher_license.establishment_placeholder", "Select establishment") },
                    ...estabOpts,
                  ]}
                  value={draftFilters.establishment}
                  disabled={!draftFilters.subprefecture}
                  onChange={(id) => updateDraft({ establishment: id })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography component="label" sx={filterLabelSx}>
                  {t("teacher_license.status", "Status")}
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={draftFilters.status}
                    onChange={(e) => updateDraft({ status: e.target.value })}
                    sx={{ borderRadius: "10px", bgcolor: figmaTokens.surface }}
                  >
                    <MenuItem value={STATUS_ALL}>{t("teacher_license.status.all", "All")}</MenuItem>
                    <MenuItem value={STATUS_LICENSED}>
                      {t("teacher_license.status.licensed", "Licensed")}
                    </MenuItem>
                    <MenuItem value={STATUS_PENDING}>
                      {t("teacher_license.status.pending", "Pending")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography component="label" sx={filterLabelSx}>
                  {t("teacher_license.matricule", "Matricule")}
                </SoftTypography>
                <SoftInput
                  placeholder={t("teacher_license.matricule_placeholder", "Search on current page…")}
                  value={draftFilters.matricule}
                  onChange={(e) => updateDraft({ matricule: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6} display="flex" alignItems="flex-end" gap={1} flexWrap="wrap">
                <SoftButton variant="gradient" color="info" onClick={handleApplyFilters}>
                  {t("teacher_license.apply", "Apply")}
                </SoftButton>
                <SoftButton variant="outlined" color="secondary" onClick={handleResetFilters}>
                  {t("teacher_license.reset", "Reset")}
                </SoftButton>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              {t(
                "teacher_license.bulk.geo_note",
                "Bulk actions only affect teachers with an active establishment assignment in the selected area."
              )}
            </Alert>
          </Card>
        </SoftBox>

        {!readOnly && <SoftBox px={{ xs: 2, md: 3 }} mb={2} display="flex" gap={1} flexWrap="wrap">
          <SoftButton variant="gradient" color="success" onClick={() => openBulkConfirm(true)}>
            {t("teacher_license.bulk.activate", "Activate filtered")}
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={() => openBulkConfirm(false)}>
            {t("teacher_license.bulk.deactivate", "Deactivate filtered")}
          </SoftButton>
        </SoftBox>}

        {error ? (
          <SoftBox px={{ xs: 2, md: 3 }} mb={2}>
            <Alert severity="error">{error}</Alert>
          </SoftBox>
        ) : null}

        <Card sx={{ mx: { xs: 2, md: 3 }, borderRadius: "16px", border: `1px solid ${figmaTokens.borderSubtle}` }}>
          <TableContainer component={Paper} elevation={0} sx={tableShellSx}>
            <Table stickyHeader size="small">
              <TableHead sx={directoryTableHeadStickyOverridesSx}>
                <TableRow>
                  {[
                    t("teacher_license.col.reg", "Matricule"),
                    t("teacher_license.col.name", "Name"),
                    t("teacher_license.col.phone", "Phone"),
                    t("teacher_license.col.province", "Province"),
                    t("teacher_license.col.department", "Department"),
                    t("teacher_license.col.subprefecture", "Sub-prefecture"),
                    t("teacher_license.col.establishment", "Establishment"),
                    t("teacher_license.col.reg_date", "Reg. date"),
                    t("teacher_license.col.type", "Type"),
                    t("teacher_license.col.status", "Status"),
                    t("teacher_license.col.actions", "Actions"),
                  ].map((label) => (
                    <TableCell key={label} sx={directoryTableHeaderCellSx}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4, ...directoryTableBodyCellDefaultsSx }}>
                      {t("teacher_license.none", "No teachers found.")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => {
                    const { id } = resolveTeacherDatabaseId(row);
                    const rowKey = id ?? row?.regNumber ?? row?.RegNumber ?? row?.id;
                    const isTeacher = readIsTeacher(row?.isTeacher ?? row?.IsTeacher);
                    const licensed = readIsLicensed(row?.isLicensed ?? row?.IsLicensed);
                    return (
                      <TableRow key={rowKey} hover>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {row?.regNumber ?? row?.RegNumber ?? "--"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          <TeacherProfileLink id={id} regNumber={row?.regNumber ?? row?.RegNumber}>
                            <SoftBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
                              <TeacherAvatar teacher={row} size="xs" />
                              <SoftTypography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: figmaTokens.accent,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {teacherFullName(row)}
                              </SoftTypography>
                            </SoftBox>
                          </TeacherProfileLink>
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {row?.phone ?? row?.Phone ?? "--"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {row?.provinceName ?? row?.ProvinceName ?? "--"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {row?.departmentName ?? row?.DepartmentName ?? "--"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {row?.subPrefectureName ?? row?.SubPrefectureName ?? "--"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {row?.establishmentName ?? row?.EstablishmentName ?? "--"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {formatDate(row?.regDate ?? row?.RegDate)}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          <Chip
                            size="small"
                            label={
                              isTeacher
                                ? t("teachers.type.teacher", "Teacher")
                                : t("teachers.type.staff", "Staff")
                            }
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              bgcolor: isTeacher ? "rgba(102, 126, 234, 0.12)" : "rgba(148, 163, 184, 0.18)",
                              color: isTeacher ? figmaTokens.accent : figmaTokens.textSecondary,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {!readOnly && isTeacher ? (
                            <Chip
                              size="small"
                              label={
                                licensed
                                  ? t("teacher_license.status.licensed", "Licensed")
                                  : t("teacher_license.status.pending", "Pending activation")
                              }
                              sx={licenseChipSx(licensed)}
                            />
                          ) : !readOnly ? (
                            <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                              {t("teacher_license.not_applicable", "N/A")}
                            </SoftTypography>
                          ) : "—"}
                        </TableCell>
                        <TableCell sx={directoryTableBodyCellDefaultsSx}>
                          {isTeacher ? (
                            <Button
                              size="small"
                              variant={licensed ? "outlined" : "contained"}
                              color={licensed ? "warning" : "success"}
                              onClick={() => handleRowToggle(row)}
                            >
                              {licensed
                                ? t("teacher_license.action.deactivate", "Deactivate")
                                : t("teacher_license.action.activate", "Activate")}
                            </Button>
                          ) : (
                            <Tooltip
                              title={t(
                                "teacher_license.staff_not_applicable",
                                "License activation applies to teachers only."
                              )}
                            >
                              <span>
                                <Button size="small" variant="outlined" disabled>
                                  {t("teacher_license.action.activate", "Activate")}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePaginationBar
            page={page}
            totalCount={totalResults}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeLabel={t("pagination.rows_per_page", "Rows per page")}
          />
        </Card>
      </SoftBox>
      <Footer />

      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle>
          {bulkAction === "activate"
            ? t("teacher_license.bulk.confirm_activate", "Activate filtered teachers?")
            : t("teacher_license.bulk.confirm_deactivate", "Deactivate filtered teachers?")}
        </DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" sx={{ mb: 1.5 }}>
            {t("teacher_license.bulk.scope", "Scope:")}
          </SoftTypography>
          <SoftTypography variant="body2" fontWeight="medium">
            {geoScopeLabel(appliedFilters)}
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkConfirmOpen(false)}>
            {t("teacher_license.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color={bulkAction === "activate" ? "success" : "error"}
            onClick={handleBulkConfirm}
          >
            {t("teacher_license.confirm", "Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default TeacherLicense;
